// Utility functions for handling different alphabets based on language

// English alphabet
const ENGLISH_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

// Complete Arabic alphabet (35 letters including all forms)
const ARABIC_ALPHABET = [
  // Basic 28 Arabic letters in traditional order
  'ا', 'ب', 'ت', 'ث', 'ج', 'ح', 'خ', 'د', 'ذ', 'ر', 'ز', 'س', 'ش', 'ص', 'ض', 'ط', 'ظ', 'ع', 'غ', 'ف', 'ق', 'ك', 'ل', 'م', 'ن', 'ه', 'و', 'ي',
  // Additional forms and letters (7 more)
  'آ',  // Alif with Madda above
  'أ',  // Alif with Hamza above  
  'إ',  // Alif with Hamza below
  'ء',  // Hamza
  'ؤ',  // Waw with Hamza above
  'ئ',  // Yeh with Hamza above
  'ة'   // Teh Marbuta
];

/**
 * Get the appropriate alphabet based on language
 * @param {string} language - Language code (e.g., 'en', 'ar')
 * @param {boolean} forceCrowdModeEnglish - Force English in crowd mode (default: false)
 * @returns {Array} Array of letters for the specified language
 */
export const getAlphabetForLanguage = (language, forceCrowdModeEnglish = false) => {
  try {
    // Force English alphabet in crowd mode if requested (now disabled by default)
    if (forceCrowdModeEnglish && isCrowdMode()) {
      return [...ENGLISH_ALPHABET];
    }
    
    switch (language) {
      case 'ar':
        return [...ARABIC_ALPHABET]; // Return a copy to prevent mutations
      case 'en':
      default:
        return [...ENGLISH_ALPHABET]; // Return a copy to prevent mutations
    }
  } catch (error) {
    console.warn('Error getting alphabet for language:', language, error);
    return [...ENGLISH_ALPHABET]; // Default to English on error
  }
};

/**
 * Check if a character belongs to the Arabic alphabet
 * @param {string} char - Character to check
 * @returns {boolean} True if character is Arabic
 */
export const isArabicCharacter = (char) => {
  // Use Unicode range for Arabic characters (U+0600-U+06FF) for comprehensive detection
  // This includes all Arabic letters, diacritics, and extended forms
  return /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/.test(char);
};

/**
 * Check if a character belongs to the English alphabet
 * @param {string} char - Character to check
 * @returns {boolean} True if character is English
 */
export const isEnglishCharacter = (char) => {
  return ENGLISH_ALPHABET.includes(char.toUpperCase());
};

/**
 * Normalize character for comparison (handles case sensitivity for English)
 * @param {string} char - Character to normalize
 * @param {string} language - Language code
 * @returns {string} Normalized character
 */
export const normalizeCharacter = (char, language) => {
  if (language === 'ar') {
    return char; // Arabic doesn't have case sensitivity
  }
  return char.toUpperCase(); // English - convert to uppercase
};

/**
 * Get the appropriate alphabet detection function based on language
 * @param {string} language - Language code
 * @returns {Function} Function to detect if character belongs to the language
 */
export const getCharacterDetector = (language) => {
  switch (language) {
    case 'ar':
      return isArabicCharacter;
    case 'en':
    default:
      return isEnglishCharacter;
  }
};

/**
 * Check if the current URL indicates crowd mode (contains m=cr)
 * @returns {boolean} True if in crowd mode
 */
export const isCrowdMode = () => {
  try {
    return window.location.href.includes('m=cr');
  } catch (error) {
    console.warn('Error checking crowd mode:', error);
    return false;
  }
};

/**
 * Auto-detect language based on the word content
 * @param {string} word - Word to analyze
 * @param {boolean} forceCrowdModeEnglish - Force English in crowd mode (default: false)
 * @returns {string} Detected language code ('ar' or 'en')
 */
export const detectLanguageFromWord = (word, forceCrowdModeEnglish = false) => {
  if (!word || typeof word !== 'string' || word.trim() === '') {
    return 'en';
  }
  
  // Force English alphabet in crowd mode if requested (now disabled by default)
  if (forceCrowdModeEnglish && isCrowdMode()) {
    return 'en';
  }
  
  try {
    const arabicChars = word.split('').filter(char => isArabicCharacter(char)).length;
    const englishChars = word.split('').filter(char => isEnglishCharacter(char)).length;
    
    // If more than 50% of characters are Arabic, consider it Arabic
    return arabicChars > englishChars ? 'ar' : 'en';
  } catch (error) {
    console.warn('Error detecting language from word:', error);
    return 'en'; // Default to English on error
  }
};